import { assetName } from "@/components/assets/asset-presenter";
import {
  Field,
  SelectInput,
  TextInput,
} from "@/components/builder/inspector-fields";
import { useAssets } from "@/lib/queries";
import { useContextStore } from "@/stores/context";
import { Image, ImageOff, Link } from "lucide-react";

// The assets library already existed and the builder could not reach it — the only way to point an
// `image` at anything was to paste a URL, next to an uploads page nobody could get to from here.
//
// The URL field stays: an `image.source` is `{kind:'url', url}` on the wire, and a customer's own CDN
// is a legitimate source. The picker just fills it in.

const CUSTOM = "__custom__";

export function AssetField({
  label,
  url,
  assetId,
  error,
  onChange,
  onAssetChange,
}: {
  label: string;
  url: string;
  assetId?: string | undefined;
  error?: string | undefined;
  onChange: (url: string) => void;
  onAssetChange?: ((assetId: string) => void) | undefined;
}): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: assets } = useAssets(appId);
  // A font in an <img> is not a thing. Only image-ish assets are offered.
  const images = (assets ?? []).filter((a) =>
    a.content_type.startsWith("image/")
  );
  const current = images.find((asset) => asset.id === assetId);
  const sourceOptions = [
    CUSTOM,
    ...(assetId && !current ? [assetId] : []),
    ...images.map((asset) => asset.id),
  ];
  const sourceLabels = {
    [CUSTOM]: "External URL",
    ...(assetId && !current ? { [assetId]: "Unavailable asset" } : {}),
    ...Object.fromEntries(images.map((asset) => [asset.id, assetName(asset)])),
  };
  const sourceIcons = {
    [CUSTOM]: Link,
    ...(assetId && !current ? { [assetId]: ImageOff } : {}),
    ...Object.fromEntries(images.map((asset) => [asset.id, Image])),
  };

  return (
    <>
      {images.length > 0 || assetId ? (
        <Field
          label="source"
          error={assetId && !current ? "This asset is unavailable." : undefined}
        >
          <SelectInput
            aria-label="asset source"
            title={current ? assetName(current) : undefined}
            value={assetId ?? CUSTOM}
            options={sourceOptions}
            labels={sourceLabels}
            icons={sourceIcons}
            onChange={(id) => {
              if (id === CUSTOM) {
                onChange(current?.url ?? "");
                return;
              }
              const asset = images.find((a) => a.id === id);
              if (asset) {
                if (onAssetChange) onAssetChange(asset.id);
                else onChange(asset.url);
              }
            }}
          />
        </Field>
      ) : null}
      {!assetId ? (
        <Field label={label} error={error}>
          <TextInput
            aria-label={label}
            placeholder="https://…"
            value={url}
            onChange={onChange}
          />
        </Field>
      ) : null}
    </>
  );
}
